import React, {useCallback, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import {CM_COLLECTION_METHOD_OPTIONS} from '../collectionTaskPayload';
import {dedupeFiles} from '../../../utils/sourceImportHelpers';
import {extractDroppedUploadEntries} from '../../products/reportEditorPage/leftPanel/components/sourcesDropUtils';
import {
    buildUploadAcceptAttribute,
    getAllowedUploadExtensions,
    isAllowedUploadFile,
} from '../../products/reportEditorPage/leftPanel/components/sourcesUploadExtensions';
import useCollectionMaterialUploadQueueStore from './useCollectionMaterialUploadQueueStore';
import InheritedClassificationDisplay from './InheritedClassificationDisplay';

// Mirrors Sources' "Add source: Files" dialog (SourcesFileImportDialog) for file intake and
// background upload queueing, but Classification Level/Distribution/Compartment are inherited
// from the parent Collection Task and are not editable here — edit the task instead. Submit
// hands files off to `useCollectionMaterialUploadQueueStore` and closes immediately.
// Drag&drop directly onto the already-open dialog mirrors Sources (see
// `handleDialogDragOver`/`handleDialogDrop` below). Only files with extensions supported by
// the platform are accepted; others are shown in a rejection panel like SourcesFileImportDialog.
const MAX_FILES = 50;

function getExtension(file) {
    if (!file?.name) return '';
    const name = String(file.name).toLowerCase();
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot) : '';
}

/**
 * Dedicated "Add Material" dialog for the Files entry point.
 * Enqueues each selected file into the Collection Material upload queue
 * (`useCollectionMaterialUploadQueueStore`) and closes immediately — the actual upload runs in
 * the background via `useCollectionMaterialUploadQueue`, mounted at `CollectionLayout` scope so
 * it survives navigating away from this task.
 *
 * Classification is locked to the parent Collection Task's markings
 * (`initialClassificationLevel` / `initialDistribution` / `initialCompartment`). A single-file
 * selection exposes an editable Title field pre-filled with the filename; multi-file batches
 * use each file's name as the material title (Title field hidden).
 *
 * Unlike `SourcesFileImportDialog`, file selection here is NOT filtered through
 * `isAllowedUploadFile`/`sourcesUploadExtensions` — intentionally. Collection Material uploads
 * go through `CollectionMaterialService.createFromFile` → `DataItemUploadV2Service.acceptUpload`
 * directly, which has no file-type gate and falls back to the legacy store-only path for types
 * outside the "pipeline V2" set. Sources uploads instead go through
 * `DataItemUploadController`, which rejects non-pipeline-V2 types outright
 * (`isPipelineV2Candidate`) — hence that dialog's client-side filter and unsupported-format
 * rejection UI. Don't add the same filter here without also relaxing/changing the backend gate,
 * or you'll block file types the backend would otherwise accept.
 */
export default function CollectionMaterialFileImportDialog({
    open,
    onClose,
    busy = false,
    initialClassificationLevel = 'U',
    initialDistribution = [],
    initialCompartment = [],
    collectionTaskId,
    initialFiles = null,
}) {
    const enqueueUploads = useCollectionMaterialUploadQueueStore((state) => state.enqueueUploads);
    const [collectionMethod, setCollectionMethod] = useState('OSINT');
    // Single source of truth: [{file, title}]. Title is pre-filled from the filename on add
    // and remains independently editable per row. Merging both into one array eliminates the
    // files/perFileTitle sync bug caused by calling setPerFileTitle inside setFiles's updater.
    const [entries, setEntries] = useState([]);

    const [error, setError] = useState(null);
    // Files rejected for unsupported extension — shape: {file, ext}[]
    const [rejectedFiles, setRejectedFiles] = useState([]);
    // Extensions the user already clicked "Request support" for
    const [requestedExts, setRequestedExts] = useState(new Set());
    const fileInputRef = useRef(null);

    // Drag-and-drop directly onto the *already-open* dialog (independent from the
    // pre-population `initialFiles` path, which is populated by dropping onto the Collected
    // Materials section before the dialog opens). MUI's Dialog portals to document.body,
    // outside CollectionTaskDetailPage's DOM subtree, so that section's drop handlers never
    // fire for drops on the open dialog — this gives the dialog its own drop target, mirroring
    // Sources' SourcesFileImportDialog.
    const [isDragOver, setIsDragOver] = useState(false);
    const dragDepthRef = useRef(0);

    const inheritedLevel = initialClassificationLevel || 'U';
    const inheritedDistribution = Array.isArray(initialDistribution) ? initialDistribution : [];
    const inheritedCompartment = Array.isArray(initialCompartment) ? initialCompartment : [];

    // Pre-populate when opened from drag&drop on the Collected Materials section.
    React.useEffect(() => {
        if (open && initialFiles && initialFiles.length) {
            const allowed = initialFiles.filter(isAllowedUploadFile);
            const rejected = initialFiles
                .filter((f) => !isAllowedUploadFile(f))
                .map((f) => ({file: f, ext: getExtension(f)}));
            const deduped = dedupeFiles(allowed).slice(0, MAX_FILES);
            setEntries(deduped.map((f) => ({file: f, title: f.name || ''})));
            setRejectedFiles(rejected);
            if (initialFiles.length > MAX_FILES) {
                setError(`You can add up to ${MAX_FILES} files at a time. Only the first ${MAX_FILES} were kept.`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFiles]);

    const resetState = useCallback(() => {
        setCollectionMethod('OSINT');
        setEntries([]);
        setRejectedFiles([]);
        setRequestedExts(new Set());
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleClose = useCallback(() => {
        if (busy) return;
        resetState();
        onClose?.();
    }, [busy, onClose, resetState]);

    // Shared file-intake pipeline: allowed/rejected split, dedupe, cap at MAX_FILES.
    // Called by both the native file picker and dialog-level drag&drop.
    // Computation happens outside any updater function so there are zero nested
    // setState calls — the pattern that broke under React Strict Mode double-invocation.
    const addIncomingFiles = useCallback((incoming) => {
        const selected = Array.from(incoming || []);
        if (!selected.length) return;

        const allowed = selected.filter(isAllowedUploadFile);
        const newRejected = selected
            .filter((f) => !isAllowedUploadFile(f))
            .map((f) => ({file: f, ext: getExtension(f)}));

        if (newRejected.length) {
            setRejectedFiles((prev) => {
                const existingNames = new Set(prev.map((r) => r.file.name));
                const fresh = newRejected.filter((r) => !existingNames.has(r.file.name));
                return fresh.length ? [...prev, ...fresh] : prev;
            });
        }

        if (!allowed.length) return;

        // Use functional updater so React always passes the latest entries snapshot —
        // the return value is the only side-effect; error state is set separately after
        // the fact (two-setter batch is fine; only *nested* setters cause problems).
        let cappedCount = 0;
        let overflowed = false;
        setEntries((prev) => {
            const existingFiles = prev.map((e) => e.file);
            const combined = dedupeFiles([...existingFiles, ...allowed]);
            cappedCount = Math.min(combined.length, MAX_FILES);
            overflowed = combined.length > MAX_FILES;
            const capped = combined.slice(0, MAX_FILES);
            return capped.map((f) => {
                const existing = prev.find((e) => e.file === f);
                return existing ?? {file: f, title: f.name || ''};
            });
        });
        // Set error AFTER setEntries, outside the updater — no nesting, no double-call.
        setError(overflowed
            ? `You can add up to ${MAX_FILES} files at a time. Only the first ${MAX_FILES} were kept.`
            : null,
        );
    }, []);

    const handleFilesInputChange = useCallback((event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        addIncomingFiles(selected);
    }, [addIncomingFiles]);

    // Gate on file-carrying drags only — ignore text/element drags so we don't interfere with
    // any other in-dialog drag interaction.
    const isFileDragEvent = useCallback((event) => {
        const types = event?.dataTransfer?.types;
        return Array.isArray(types) ? types.includes('Files') : types?.contains?.('Files');
    }, []);

    const resetDragState = useCallback(() => {
        dragDepthRef.current = 0;
        setIsDragOver(false);
    }, []);

    const handleDialogDragOver = useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        event.preventDefault();
        // Explicitly set dropEffect — some browser/OS combos (real native OS file drags,
        // unlike CDP-synthesized DragEvents) render a "not-allowed" cursor on dragover unless
        // dropEffect is set every time, even though preventDefault() alone is enough to permit
        // the drop programmatically.
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    }, [isFileDragEvent]);

    const handleDialogDragEnter = useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        dragDepthRef.current += 1;
        setIsDragOver(true);
    }, [isFileDragEvent]);

    const handleDialogDragLeave = useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragOver(false);
        }
    }, [isFileDragEvent]);

    const handleDialogDrop = useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        // preventDefault synchronously — dataTransfer items are only valid for the duration of
        // the synchronous event dispatch in some browsers, so capture what we need before the
        // async extractDroppedUploadEntries walk.
        event.preventDefault();
        const dataTransfer = event.dataTransfer;
        resetDragState();

        extractDroppedUploadEntries(dataTransfer)
            .then((entries) => {
                const droppedFiles = entries.map((entry) => entry.file).filter(Boolean);
                if (droppedFiles.length) {
                    addIncomingFiles(droppedFiles);
                }
            })
            .catch((dropError) => {
                console.error('[CollectionMaterialFileImportDialog] Failed to process dropped files:', dropError);
                setError(dropError?.message || 'Failed to read dropped files.');
            });
    }, [addIncomingFiles, isFileDragEvent, resetDragState]);

    const handleRemoveFile = useCallback((index) => {
        setEntries((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleDismissRejected = useCallback((index) => {
        setRejectedFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleRequestSupport = useCallback((ext) => {
        setRequestedExts((prev) => new Set([...prev, ext]));
    }, []);

    const handleFileTitleChange = useCallback((index, value) => {
        setEntries((prev) => {
            const next = [...prev];
            next[index] = {...next[index], title: value};
            return next;
        });
    }, []);

    const handleSubmit = useCallback(() => {
        setError(null);
        if (!entries.length) {
            setError('Choose at least one file to upload.');
            return;
        }
        if (!collectionTaskId) {
            setError('Collection Task is required to upload files.');
            return;
        }

        enqueueUploads(entries.map((e) => e.file), {
            collectionTaskId,
            collectionMethod,
            // Per-file titles: fall back to filename if the user cleared the field.
            titles: entries.map((e) => (e.title.trim() || e.file.name || undefined)),
            // Every file inherits the parent task's markings — no per-file override.
            classifications: entries.map(() => ({
                level: inheritedLevel,
                distribution: [...inheritedDistribution],
                compartment: [...inheritedCompartment],
            })),
        });

        resetState();
        onClose?.();
    }, [
        collectionMethod,
        collectionTaskId,
        enqueueUploads,
        entries,
        inheritedCompartment,
        inheritedDistribution,
        inheritedLevel,
        onClose,
        resetState,
    ]);

    const submitDisabled = busy || !entries.length;

    const allowedExtensions = React.useMemo(() => getAllowedUploadExtensions(), []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            slotProps={{
                paper: {
                    // Attach all drag handlers at the Paper level so the entire dialog
                    // surface is a drop zone. We also suppress the browser's default
                    // "drop text into input" behaviour at the capture phase so TextField
                    // rows never intercept file drops before our handler can see them.
                    onDragOverCapture: handleDialogDragOver,
                    onDragEnterCapture: handleDialogDragEnter,
                    onDragLeaveCapture: handleDialogDragLeave,
                    onDropCapture: handleDialogDrop,
                    sx: {position: 'relative'},
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AttachFileRoundedIcon fontSize="small" color="action"/>
                    <span>Add Collected Material: Files</span>
                    <Typography variant="body2" color="text.secondary" sx={{ml: 'auto !important'}}>
                        {entries.length > 0 ? `${entries.length} file${entries.length === 1 ? '' : 's'} selected` : ''}
                    </Typography>
                </Stack>
            </DialogTitle>

            {isDragOver && (
                <Box
                    data-testid="collection-material-file-dialog-drop-overlay"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        bgcolor: 'rgba(233, 236, 239, 0.5)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        boxShadow: 'inset 0 0 0 2px #1976d2',
                    }}
                >
                    <Box sx={{
                        bgcolor: 'rgba(25, 118, 210, 0.96)',
                        color: 'white',
                        borderRadius: 1,
                        px: 2,
                        py: 1.25,
                        textAlign: 'center',
                        boxShadow: 3,
                    }}>
                        <Typography variant="body2" fontWeight={600}>
                            Drop files to add
                        </Typography>
                    </Box>
                </Box>
            )}

            <DialogContent dividers sx={{minHeight: 420}}>
                <Stack spacing={2}>
                    {/* Description + file picker — explicitly calls out drag&drop support so
                        users don't have to discover it by accident (the highlighted overlay
                        only appears once a drag is already in progress). */}
                    <Box sx={{
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'grey.50',
                    }}>
                        <CloudUploadRoundedIcon color="action" sx={{flexShrink: 0}}/>
                        <Stack spacing={0} sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="body2" fontWeight={600}>
                                Drag and drop files anywhere in this window
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Upload up to {MAX_FILES} files. Classification is inherited from the Collection Task.
                                {' '}Supported: {allowedExtensions.join(', ')}
                                {' · '}
                                <Box component="span"
                                    sx={{cursor: 'pointer', textDecoration: 'underline'}}
                                    onClick={() => window.open('mailto:support@dmetrics.com?subject=Request%20file%20format%20support', '_blank')}
                                >
                                    Request support
                                </Box>
                            </Typography>
                        </Stack>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AttachFileRoundedIcon/>}
                            sx={{flexShrink: 0}}
                        >
                            Choose files
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                accept={buildUploadAcceptAttribute()}
                                onChange={handleFilesInputChange}
                            />
                        </Button>
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="cm-file-import-method-label">Collection Method</InputLabel>
                            <Select
                                labelId="cm-file-import-method-label"
                                label="Collection Method"
                                value={collectionMethod}
                                onChange={(e) => setCollectionMethod(e.target.value)}
                            >
                                {CM_COLLECTION_METHOD_OPTIONS.map((method) => (
                                    <MenuItem key={method} value={method}>{method}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    {/* ── Unsupported-format rejections ─────────────────────────────── */}
                    {rejectedFiles.length > 0 && (
                        <Box sx={{
                            border: '1px solid',
                            borderColor: 'warning.light',
                            borderRadius: 1,
                            overflow: 'hidden',
                        }}>
                            <Box sx={{
                                px: 2,
                                py: 1,
                                bgcolor: 'warning.50',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                                <BlockRoundedIcon fontSize="small" sx={{color: 'warning.main'}}/>
                                <Typography variant="body2" fontWeight={600} sx={{color: 'warning.dark'}}>
                                    {rejectedFiles.length} unsupported format{rejectedFiles.length === 1 ? '' : 's'} — these files cannot be uploaded
                                </Typography>
                            </Box>

                            {rejectedFiles.map(({file, ext}, index) => {
                                const alreadyRequested = requestedExts.has(ext);
                                return (
                                    <React.Fragment key={`${file.name}-${file.size}-${index}`}>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto auto',
                                            gap: 1,
                                            px: 2,
                                            py: 1,
                                            alignItems: 'center',
                                        }}>
                                            <Stack spacing={0} sx={{minWidth: 0}}>
                                                <Typography variant="body2" noWrap title={file.name} sx={{color: 'text.secondary'}}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled">
                                                    {ext || 'no extension'} · {Math.max(1, Math.round(file.size / 1024))} KB
                                                </Typography>
                                            </Stack>
                                            <Button
                                                size="small"
                                                variant="text"
                                                disabled={alreadyRequested}
                                                onClick={() => handleRequestSupport(ext)}
                                                sx={{whiteSpace: 'nowrap', color: 'warning.dark'}}
                                            >
                                                {alreadyRequested ? 'Requested ✓' : 'Request support'}
                                            </Button>
                                            <Tooltip title="Dismiss">
                                                <IconButton
                                                    size="small"
                                                    aria-label="dismiss unsupported file"
                                                    onClick={() => handleDismissRejected(index)}
                                                >
                                                    <CloseRoundedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {index < rejectedFiles.length - 1 && <Divider/>}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    )}

                    <InheritedClassificationDisplay
                        level={inheritedLevel}
                        distribution={inheritedDistribution}
                        compartment={inheritedCompartment}
                    />

                    {/* ── Selected-file list ─────────────────────────────────────────── */}
                    {entries.length > 0 && (
                        <Box sx={{
                            maxHeight: 480,
                            overflowY: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                        }}>
                            {/* Sticky header */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 36px',
                                gap: 1,
                                px: 2,
                                py: 0.75,
                                bgcolor: 'grey.50',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>File</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Title</Typography>
                                <Box/>
                            </Box>

                            {entries.map(({file, title}, index) => (
                                <React.Fragment key={`${file.name}-${file.size}-${index}`}>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr 36px',
                                        gap: 1,
                                        px: 2,
                                        py: 1,
                                        alignItems: 'center',
                                        '&:hover': {bgcolor: 'grey.50'},
                                    }}>
                                        {/* File name + size */}
                                        <Stack spacing={0} sx={{minWidth: 0}}>
                                            <Typography variant="body2" noWrap title={file.name} sx={{fontWeight: 500}}>
                                                {file.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {Math.max(1, Math.round(file.size / 1024))} KB
                                            </Typography>
                                        </Stack>

                                        {/* Per-file editable title, pre-filled with filename */}
                                        <TextField
                                            size="small"
                                            value={title}
                                            onChange={(e) => handleFileTitleChange(index, e.target.value)}
                                            inputProps={{'aria-label': `Title for ${file.name}`}}
                                            placeholder={file.name}
                                        />

                                        {/* Remove */}
                                        <Tooltip title="Remove">
                                            <IconButton
                                                size="small"
                                                aria-label="remove file"
                                                onClick={() => handleRemoveFile(index)}
                                            >
                                                <CloseRoundedIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    {index < entries.length - 1 && <Divider/>}
                                </React.Fragment>
                            ))}
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error">
                            {error}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={busy}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
                    {busy ? 'Importing…' : `Add ${entries.length > 0 ? `${entries.length} material${entries.length === 1 ? '' : 's'}` : 'material'}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
